import { api } from '../config/api';

export interface Filial {
  id: string;
  nome: string;
  cidade: string;
  estado: string;
  endereco?: string;
  telefone?: string;
  status: 'ativo' | 'inativo';
  criado_em: string;
  atualizado_em: string;
}

class FiliaisServiceAPI {
  // Buscar todas as filiais
  static async buscarTodas(ativasOnly = true): Promise<Filial[]> {
    try {
      const response = await api.get(`/filiais?ativasOnly=${ativasOnly}`);
      return response.data || [];
    } catch (error) {
      console.error('Erro ao buscar filiais:', error);
      throw error;
    }
  }

  // Buscar filial por ID
  static async buscarPorId(id: string): Promise<Filial | null> {
    try {
      const response = await api.get(`/filiais/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar filial:', error);
      return null;
    }
  }

  // Criar filial
  static async criar(filial: Omit<Filial, 'id' | 'criado_em' | 'atualizado_em'>): Promise<Filial> {
    try {
      const response = await api.post('/filiais', filial);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar filial:', error);
      throw error;
    }
  }

  // Atualizar filial
  static async atualizar(id: string, updates: Partial<Filial>): Promise<Filial> {
    try {
      const response = await api.put(`/filiais/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar filial:', error);
      throw error;
    }
  }

  // Deletar filial
  static async deletar(id: string): Promise<void> {
    try {
      await api.delete(`/filiais/${id}`);
    } catch (error) {
      console.error('Erro ao deletar filial:', error);
      throw error;
    }
  }

  // Buscar filiais ativas (método de conveniência)
  static async buscarAtivas(): Promise<Filial[]> {
    return this.buscarTodas(true);
  }

  // Buscar filiais por estado
  static async buscarPorEstado(estado: string): Promise<Filial[]> {
    try {
      const response = await api.get('/filiais');
      const todasFiliais = response.data || [];
      return todasFiliais.filter((filial: Filial) => filial.estado === estado);
    } catch (error) {
      console.error('Erro ao buscar filiais por estado:', error);
      throw error;
    }
  }

  // Buscar filiais por cidade
  static async buscarPorCidade(cidade: string): Promise<Filial[]> {
    try {
      const response = await api.get('/filiais');
      const todasFiliais = response.data || [];
      return todasFiliais.filter((filial: Filial) => 
        filial.cidade.toLowerCase().includes(cidade.toLowerCase())
      );
    } catch (error) {
      console.error('Erro ao buscar filiais por cidade:', error);
      throw error;
    }
  }

  // Verificar se filial existe
  static async existe(id: string): Promise<boolean> {
    try {
      const filial = await this.buscarPorId(id);
      return filial !== null;
    } catch (error) {
      return false;
    }
  }

  // Ativar/Desativar filial
  static async alternarStatus(id: string): Promise<Filial> {
    try {
      const filial = await this.buscarPorId(id);
      if (!filial) {
        throw new Error('Filial não encontrada');
      }

      const novoStatus = filial.status === 'ativo' ? 'inativo' : 'ativo';
      return await this.atualizar(id, { status: novoStatus });
    } catch (error) {
      console.error('Erro ao alternar status da filial:', error);
      throw error;
    }
  }
}

export default FiliaisServiceAPI;
